/**
 * A step's operations and how it finishes (D45): what each pump does (off,
 * run at a rate, a rate and a volume or a time, or ramp segments), mixer,
 * thermal zone and regulator setpoints, and the finish, whichever comes first of the
 * operations completing, a condition, and a maximum time.
 *
 * Rates and volumes are typed in the design's display units (D49), speeds in
 * rpm, temperatures in °C and regulated pressures in kPa; what is stored is
 * SI (invariant 5).
 */
import { Fragment, useState, type ReactNode } from "react";
import type { NodeDocument } from "../design/document";
import {
  COMPARISONS,
  type Condition,
  type ConditionKind,
  type Finish,
  type ProfileQuantity,
  type ProfileSegment,
  type PumpMode,
  type RampSegment,
  type RunPump,
  type RunSetpoint,
  type RunSource,
  type SetSetpoint,
  type Source,
  type Step,
} from "../design/protocol";
import {
  completing as completingOperations,
  profileLevels,
  profileSeconds,
  pumpSeconds,
  pumpVolume,
  secondsText,
  volumeText,
} from "../scripts/operations";
import { useLiquidUnits } from "./DisplayUnitsContext";
import { AddSelect, CheckRow, FieldRow, FieldTable, FoldRow, NumberField, Select, SheetRows, SheetTable, TextField } from "./fields";
import { SignalGlyph, TriggerQualifiers, triggerShape } from "./signals";
import { TUBING_SET_UNITS, type LiquidUnits } from "./units";

/** A rate, typed in the design's flow unit (D49), stored in m³/s. */
export function RateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
}) {
  const unit = useLiquidUnits().flowRate;
  return <NumberField label={label} value={value ?? null} factor={unit.factor} unit={unit.label} min={0} onChange={onChange} />;
}

/** A volume, typed in the design's volume unit (D49), stored in m³. */
function VolumeField({ label, value, onChange }: { label: string; value: number | null | undefined; onChange: (value: number | null) => void }) {
  const unit = useLiquidUnits().volume;
  return <NumberField label={label} unit={unit.label} factor={unit.factor} min={0} value={value ?? null} onChange={onChange} />;
}

const MODES: readonly { value: PumpMode | ""; label: string }[] = [
  { value: "", label: "as the step before left it" },
  { value: "off", label: "off" },
  { value: "run", label: "run at a rate" },
  { value: "volume", label: "dispense a volume" },
  { value: "time", label: "run for a time" },
  { value: "ramp", label: "ramp profile" },
];

/**
 * A fresh operation of `mode`, keeping the rate the pump had. A new one starts
 * at ten of the design's units: 10 mL/min and 10 mL on a tubing set, what a
 * peristaltic head on its tubing typically runs at.
 */
export function pumpOperation(target: string, mode: PumpMode, before?: RunPump | null, units: LiquidUnits = TUBING_SET_UNITS): RunPump {
  const rate = before?.rate ?? before?.segments?.at(-1)?.to_rate ?? 10 * units.flowRate.factor;
  const keep = mode !== "off" && Boolean(before?.keep_running);
  switch (mode) {
    case "off":
      return { kind: "pump", target, mode };
    case "run":
      return { kind: "pump", target, mode, rate, keep_running: keep };
    case "volume":
      return { kind: "pump", target, mode, rate, volume: before?.volume ?? 10 * units.volume.factor, keep_running: keep };
    case "time":
      return { kind: "pump", target, mode, rate, seconds: before?.seconds ?? 30, keep_running: keep };
    case "ramp":
      return {
        kind: "pump",
        target,
        mode,
        segments: before?.segments?.length ? before.segments : [{ from_rate: 0, to_rate: rate, seconds: 10 }],
        keep_running: keep,
      };
  }
}

/**
 * What one pump does in a step. `operation` null: nothing, so it is as the
 * step before left it. `onChange(null)` clears it.
 */
export function PumpFields({
  pump,
  operation,
  onChange,
  bare = false,
}: {
  pump: NodeDocument;
  operation: RunPump | null;
  onChange: (operation: RunPump | null) => void;
  /** Rows only, for a table around it. */
  bare?: boolean;
}) {
  const name = pump.label || pump.local_id;
  const units = useLiquidUnits();
  const set = (change: Partial<RunPump>) => operation && onChange({ ...operation, ...change });
  const segments = operation?.segments ?? [];
  const setSegment = (at: number, change: Partial<RampSegment>) =>
    set({ segments: segments.map((segment, index) => (index === at ? { ...segment, ...change } : segment)) });
  const seconds = operation ? pumpSeconds(operation) : null;
  const volume = operation ? pumpVolume(operation) : null;
  const rows = (
    <>
      <Select
        label={name}
        value={operation?.mode ?? ""}
        options={MODES}
        onChange={(mode) => onChange(mode ? pumpOperation(pump.local_id, mode, operation, units) : null)}
      />
      {operation && operation.mode !== "off" && operation.mode !== "ramp" && (
        <>
          <RateField label="Rate" value={operation.rate} onChange={(rate) => rate && set({ rate })} />
          {operation.mode === "volume" && (
            <VolumeField label="Volume" value={operation.volume} onChange={(volume) => volume && set({ volume })} />
          )}
          {operation.mode === "time" && (
            <NumberField label="For" unit="s" min={0} value={operation.seconds ?? null} onChange={(time) => time && set({ seconds: time })} />
          )}
        </>
      )}
      {operation?.mode === "ramp" &&
        segments.map((segment, at) => (
          <Fragment key={at}>
            <FieldRow
              label={<span className="ws-muted">Segment {at + 1}</span>}
              unit={
                <button
                  type="button"
                  className="sx-icon"
                  aria-label={`Remove segment ${at + 1}`}
                  disabled={segments.length === 1}
                  onClick={() => set({ segments: segments.filter((_, index) => index !== at) })}
                >
                  ×
                </button>
              }
            />
            <RateField label="From" value={segment.from_rate} onChange={(rate) => rate !== null && setSegment(at, { from_rate: rate })} />
            <RateField label="To" value={segment.to_rate} onChange={(rate) => rate !== null && setSegment(at, { to_rate: rate })} />
            <NumberField label="Over" unit="s" min={0} value={segment.seconds} onChange={(time) => time && setSegment(at, { seconds: time })} />
          </Fragment>
        ))}
      {operation?.mode === "ramp" && (
        <FieldRow label="" span>
          <button
            type="button"
            className="sx-small-button"
            onClick={() => {
              const last = segments.at(-1);
              set({ segments: [...segments, { from_rate: last?.to_rate ?? 0, to_rate: last?.to_rate ?? 0, seconds: 10 }] });
            }}
          >
            Add segment
          </button>
        </FieldRow>
      )}
      {operation && operation.mode !== "off" && (
        <CheckRow label="Keep running into the next step" checked={Boolean(operation.keep_running)} onChange={(keep_running) => set({ keep_running })} />
      )}
      {operation && (seconds !== null || volume !== null) && operation.mode !== "off" && (
        <FieldRow label="" span>
          <span className="ws-muted ws-small sx-mono">
            {[seconds !== null ? secondsText(seconds) : null, volume !== null ? volumeText(volume, units) : null].filter(Boolean).join(" · ")}
          </span>
        </FieldRow>
      )}
    </>
  );
  return bare ? rows : <FieldTable label={`${name} operation`}>{rows}</FieldTable>;
}

const PROFILE_MODES: readonly { value: RunSource["mode"] | ""; label: string }[] = [
  { value: "", label: "no profile" },
  { value: "stairs", label: "staircase" },
  { value: "ramp", label: "ramp" },
];

const QUANTITY_LABELS: Record<ProfileQuantity, string> = { pressure: "pressure", vacuum: "vacuum", flow: "flow" };

/** What a source's values are typed in: kPa gauge for pressure and vacuum, the design's flow unit (D49). */
function useSourceUnit(quantity: ProfileQuantity): { label: string; factor: number } {
  const flow = useLiquidUnits().flowRate;
  return quantity === "flow" ? { label: flow.label, factor: flow.factor } : { label: "kPa", factor: 1000 };
}

/**
 * A fresh profile of `mode`: a staircase from where the source's own setting
 * of that kind is, or 10 kPa, up by a tenth of it; a ramp to it over 10 s.
 */
export function sourceProfile(source: Source, quantity: ProfileQuantity, mode: RunSource["mode"], before?: RunSource | null): RunSource {
  const named = source.settings.find((setting) => setting.kind === quantity)?.magnitude;
  const sign = quantity === "vacuum" ? -1 : 1;
  const top = named ?? sign * (quantity === "flow" ? 1e-7 : 50_000);
  if (mode === "stairs") {
    const values = before?.mode === "ramp" ? (before.segments ?? []) : [];
    return {
      kind: "source_profile",
      source: source.name,
      quantity,
      mode,
      start: values[0]?.from_value ?? top / 5,
      end: values.at(-1)?.to_value ?? top,
      increment: Math.abs(top) / 10,
      hold: 10,
    };
  }
  return {
    kind: "source_profile",
    source: source.name,
    quantity,
    mode,
    segments: [{ from_value: before?.start ?? 0, to_value: before?.end ?? top, seconds: 10 }],
  };
}

/** A source's profile in a step (D52). `profile` null: none, so the source is as set. `onChange(null)` clears it. */
export function SourceProfileFields({
  source,
  quantities,
  profile,
  onChange,
  bare = false,
}: {
  source: Source;
  /** What the source can supply, from its settings' kinds. */
  quantities: readonly ProfileQuantity[];
  profile: RunSource | null;
  onChange: (profile: RunSource | null) => void;
  /** Rows only, for a table around it. */
  bare?: boolean;
}) {
  const name = source.description || source.name;
  const unit = useSourceUnit(profile?.quantity ?? quantities[0] ?? "pressure");
  const sign = profile?.quantity === "vacuum" ? -1 : 1;
  const set = (change: Partial<RunSource>) => profile && onChange({ ...profile, ...change });
  const segments = profile?.segments ?? [];
  const setSegment = (at: number, change: Partial<ProfileSegment>) =>
    set({ segments: segments.map((segment, index) => (index === at ? { ...segment, ...change } : segment)) });
  // A vacuum is typed as its depth and stored negative, as the condition set does.
  const valueField = (label: string, value: number | null | undefined, change: (value: number) => void, magnitude = false) => (
    <NumberField
      label={label}
      unit={unit.label}
      factor={unit.factor}
      min={0}
      value={value == null ? null : magnitude ? value : sign * value}
      onChange={(typed) => typed !== null && change(magnitude ? typed : sign * typed)}
    />
  );
  const levels = profile ? profileLevels(profile) : [];
  const seconds = profile ? profileSeconds(profile) : null;
  const rows = (
    <>
      <Select
        label={name}
        value={profile?.mode ?? ""}
        options={PROFILE_MODES}
        onChange={(mode) => onChange(mode ? sourceProfile(source, profile?.quantity ?? quantities[0] ?? "pressure", mode, profile) : null)}
      />
      {profile && quantities.length > 1 && (
        <Select
          label="Of"
          value={profile.quantity}
          options={quantities.map((quantity) => ({ value: quantity, label: QUANTITY_LABELS[quantity] }))}
          onChange={(quantity) => onChange(sourceProfile(source, quantity, profile.mode))}
        />
      )}
      {profile?.mode === "stairs" && (
        <>
          {valueField("From", profile.start, (start) => set({ start }))}
          {valueField("To", profile.end, (end) => set({ end }))}
          {valueField("In steps of", profile.increment, (increment) => increment > 0 && set({ increment }), true)}
          <NumberField label="Hold each" unit="s" min={0} value={profile.hold ?? null} onChange={(hold) => hold && set({ hold })} />
        </>
      )}
      {profile?.mode === "ramp" &&
        segments.map((segment, at) => (
          <Fragment key={at}>
            <FieldRow
              label={<span className="ws-muted">Segment {at + 1}</span>}
              unit={
                <button
                  type="button"
                  className="sx-icon"
                  aria-label={`Remove segment ${at + 1}`}
                  disabled={segments.length === 1}
                  onClick={() => set({ segments: segments.filter((_, index) => index !== at) })}
                >
                  ×
                </button>
              }
            />
            {valueField("From", segment.from_value, (from_value) => setSegment(at, { from_value }))}
            {valueField("To", segment.to_value, (to_value) => setSegment(at, { to_value }))}
            <NumberField label="Over" unit="s" min={0} value={segment.seconds} onChange={(time) => time && setSegment(at, { seconds: time })} />
          </Fragment>
        ))}
      {profile?.mode === "ramp" && (
        <FieldRow label="" span>
          <button
            type="button"
            className="sx-small-button"
            onClick={() => {
              const last = segments.at(-1);
              set({ segments: [...segments, { from_value: last?.to_value ?? 0, to_value: last?.to_value ?? 0, seconds: 10 }] });
            }}
          >
            Add segment
          </button>
        </FieldRow>
      )}
      {profile && seconds !== null && (
        <FieldRow label="" span>
          <span className="ws-muted ws-small sx-mono">
            {[profile.mode === "stairs" ? `${levels.length} levels` : null, secondsText(seconds)].filter(Boolean).join(" · ")}
          </span>
        </FieldRow>
      )}
    </>
  );
  return bare ? rows : <FieldTable label={`${name} profile`}>{rows}</FieldTable>;
}

/**
 * A regulator as the source-shaped thing a profile's fields edit: pressure only,
 * and `top` (its highest set pressure) where a fresh profile climbs to.
 */
function asSource(target: string, name: string, top: number | null): Source {
  return { name: target, description: name, port: "", initial: "pressure", settings: [{ name: "pressure", kind: "pressure", magnitude: top }] };
}

const toSourceProfile = (profile: RunSetpoint): RunSource => {
  const { target, kind: _kind, ...shape } = profile;
  return { ...shape, kind: "source_profile", source: target };
};

const toRegulatorProfile = (profile: RunSource, target: string): RunSetpoint => {
  const { source: _source, kind: _kind, quantity: _quantity, ...shape } = profile;
  return { ...shape, kind: "setpoint_profile", target, quantity: "pressure" };
};

/** A fresh pressure sweep for a regulator (D65), climbing to `top` (Pa gauge) or 50 kPa. */
export function regulatorProfile(target: string, mode: RunSetpoint["mode"], top: number | null): RunSetpoint {
  return toRegulatorProfile(sourceProfile(asSource(target, target, top), "pressure", mode), target);
}

/**
 * A regulator's outlet run through a staircase or a ramp in a step (D65): the
 * same fields as a source's profile, in kPa. `onChange(null)` clears it.
 */
export function RegulatorProfileFields({
  target,
  name,
  top,
  profile,
  onChange,
}: {
  target: string;
  name: string;
  top: number | null;
  profile: RunSetpoint;
  onChange: (profile: RunSetpoint | null) => void;
}) {
  return (
    <SourceProfileFields
      bare
      source={asSource(target, name, top)}
      quantities={["pressure"]}
      profile={toSourceProfile(profile)}
      onChange={(next) => onChange(next ? toRegulatorProfile(next, target) : null)}
    />
  );
}

/** How a setpoint is typed and shown, and the way to and from SI: °C over K, rpm over Hz, kPa over Pa gauge. */
export const SETPOINT_UNITS: Record<SetSetpoint["quantity"], { label: string; toSI: (shown: number) => number; fromSI: (value: number) => number }> = {
  temperature: { label: "°C", toSI: (shown) => shown + 273.15, fromSI: (value) => value - 273.15 },
  speed: { label: "rpm", toSI: (shown) => shown / 60, fromSI: (value) => value * 60 },
  pressure: { label: "kPa", toSI: (shown) => shown * 1000, fromSI: (value) => value / 1000 },
};

/** A mixer's speed in rpm, a thermal zone's temperature in °C, or a regulator's outlet in kPa. Null: unchanged. */
export function SetpointFields({
  target,
  name,
  quantity,
  setpoint,
  onChange,
}: {
  target: string;
  name: string;
  quantity: SetSetpoint["quantity"];
  setpoint: SetSetpoint | null;
  onChange: (setpoint: SetSetpoint | null) => void;
}) {
  // Shown in the unit an operator reads; stored SI.
  const unit = SETPOINT_UNITS[quantity];
  const shown = setpoint ? unit.fromSI(setpoint.value) : null;
  return (
    <NumberField
      label={name}
      unit={unit.label}
      placeholder="unchanged"
      value={shown}
      onChange={(value) => onChange(value === null ? null : { kind: "setpoint", target, quantity, value: unit.toSI(value) })}
    />
  );
}

/** What a sensor condition compares (D52): its reading, its change in the step, or its fall from its peak. */
const MEASURES: readonly { value: "reading" | "change" | "drop"; label: string }[] = [
  { value: "reading", label: "reading" },
  { value: "change", label: "change in step" },
  { value: "drop", label: "fall from peak" },
];

const CMP: Record<string, string> = { lt: "<", le: "≤", gt: ">", ge: "≥" };

/** A sensor's reading as typed and shown: its unit and the factor from SI. */
export type SensorUnit = (sensor: string) => { label: string; factor: number };

/**
 * How a step can end, as the add menu offers them. On an air detector, a
 * fluid running out is air arriving after liquid (its trailing edge), and a
 * fluid arriving is liquid after air (its leading edge), each deaf to blips.
 */
type EndpointKind = ConditionKind | "runs_out" | "arrives";

const freshCondition = (kind: EndpointKind, sensor: string, units: LiquidUnits, meter = "", vessel = ""): Condition => {
  switch (kind) {
    case "sensed":
      return { kind, sensor, comparison: "ge", value: 1 };
    case "runs_out":
      return { kind: "sensed", sensor, comparison: "ge", value: 1, edge: true, ignore: 1 };
    case "arrives":
      return { kind: "sensed", sensor, comparison: "lt", value: 1, edge: true, ignore: 1 };
    case "pressure_threshold":
      return { kind, at: sensor, comparison: "ge", pressure: 10_000 };
    case "volume_delivered":
      return { kind, through: meter, volume: units.volume.factor };
    case "operator_wait":
      return { kind, instruction: "Continue when ready" };
    case "vessel_empty":
      return { kind, vessel };
  }
};

/** What an air detector's condition reads as: air (reads 1) or liquid (reads 0), where it is one of those. */
function airOrLiquid(condition: Condition, airSensors: ReadonlySet<string>): "air" | "liquid" | null {
  if (condition.kind !== "sensed" || !airSensors.has(condition.sensor) || (condition.measure ?? "reading") !== "reading") return null;
  const { comparison: c, value: v } = condition;
  if ((c === "ge" && v === 1) || (c === "gt" && v === 0)) return "air";
  if ((c === "lt" && v === 1) || (c === "le" && v === 0)) return "liquid";
  return null;
}

/** A sensor or pressure condition's comparison and threshold, in the units it is typed in: "≥ 5 kPa", or "air". */
function thresholdText(condition: Condition, sensorUnit: SensorUnit, airSensors: ReadonlySet<string>): string {
  const fluid = airOrLiquid(condition, airSensors);
  if (fluid) return fluid;
  if (condition.kind === "pressure_threshold") return `${CMP[condition.comparison]} ${Number((condition.pressure / 1000).toPrecision(4))} kPa`;
  if (condition.kind !== "sensed") return "";
  const unit = sensorUnit(condition.sensor);
  const shown = Number((condition.value / unit.factor).toPrecision(4));
  const unitText = unit.label && !unit.label.startsWith("(") ? ` ${unit.label}` : "";
  return `${CMP[condition.comparison]} ${shown}${unitText}`;
}

/**
 * A condition in words, in the units it is typed in, for a title or a
 * screen reader: "B1 air, on an edge, held 5 s, ignoring under 1 s",
 * "P5 fall ≥ 5 kPa".
 */
export function conditionText(
  condition: Condition,
  names: Readonly<Record<string, string>>,
  sensorUnit: SensorUnit,
  airSensors: ReadonlySet<string>,
  units: LiquidUnits,
): string {
  switch (condition.kind) {
    case "sensed":
    case "pressure_threshold": {
      const name = condition.kind === "sensed" ? (names[condition.sensor] ?? condition.sensor) : (names[condition.at] ?? condition.at);
      const measure = condition.kind === "sensed" ? { reading: "", change: " change", drop: " fall" }[condition.measure ?? "reading"] : "";
      return [
        `${name}${measure} ${thresholdText(condition, sensorUnit, airSensors)}`,
        condition.edge ? "on an edge" : null,
        condition.hold ? `held ${secondsText(condition.hold)}` : null,
        condition.ignore ? `ignoring under ${secondsText(condition.ignore)}` : null,
      ]
        .filter(Boolean)
        .join(", ");
    }
    case "volume_delivered":
      return `${volumeText(condition.volume, units)} through ${names[condition.through] ?? (condition.through || "?")}`;
    case "operator_wait":
      return "operator continues";
    case "vessel_empty":
      return `${names[condition.vessel] ?? condition.vessel} runs empty`;
  }
}

/**
 * A condition as glyphs: its trigger's trace, the sensor, what it compares,
 * then how long it holds and what it ignores. Says it in words to a screen
 * reader and on hover.
 */
export function ConditionGlyphs({
  condition,
  names,
  sensorUnit = NO_UNIT,
  airSensors = NONE,
  units,
}: {
  condition: Condition;
  names: Readonly<Record<string, string>>;
  sensorUnit?: SensorUnit;
  airSensors?: ReadonlySet<string>;
  units: LiquidUnits;
}) {
  const words = conditionText(condition, names, sensorUnit, airSensors, units);
  let body: ReactNode;
  switch (condition.kind) {
    case "sensed":
    case "pressure_threshold": {
      const name = condition.kind === "sensed" ? (names[condition.sensor] ?? condition.sensor) : (names[condition.at] ?? condition.at);
      const measure = condition.kind === "sensed" ? (condition.measure ?? "reading") : "reading";
      body = (
        <>
          <SignalGlyph shape={triggerShape(condition)!} />
          {name}
          {measure === "change" ? " Δ" : null}
          {measure === "drop" ? <SignalGlyph shape="drop" /> : " "}
          {thresholdText(condition, sensorUnit, airSensors)}
          <TriggerQualifiers hold={condition.hold} ignore={condition.ignore} />
        </>
      );
      break;
    }
    case "volume_delivered":
      body = (
        <>
          <SignalGlyph shape="volume" />
          {volumeText(condition.volume, units)}
        </>
      );
      break;
    case "operator_wait":
      body = (
        <>
          <SignalGlyph shape="operator" />
          operator
        </>
      );
      break;
    case "vessel_empty":
      body = (
        <>
          <SignalGlyph shape="volume" />
          {`${names[condition.vessel] ?? condition.vessel} empty`}
        </>
      );
  }
  return (
    <span className="sx-glyphs" role="img" aria-label={words} title={words}>
      {body}
    </span>
  );
}

const NO_UNIT: SensorUnit = () => ({ label: "", factor: 1 });
const NONE: ReadonlySet<string> = new Set();

/** How a step ends, as glyphs, whichever comes first: completion, conditions, the maximum time. */
export function FinishGlyphs({
  step,
  names,
  sensorUnit,
  airSensors,
  units,
}: {
  step: Step;
  names: Readonly<Record<string, string>>;
  sensorUnit?: SensorUnit;
  airSensors?: ReadonlySet<string>;
  units: LiquidUnits;
}) {
  const finish = step.finish ?? {};
  const parts: ReactNode[] = [];
  if (finish.complete)
    parts.push(
      <span className="sx-glyphs" role="img" aria-label="done" title="When its operations complete">
        <SignalGlyph shape="done" />
      </span>,
    );
  for (const condition of finish.conditions ?? [])
    parts.push(<ConditionGlyphs condition={condition} names={names} sensorUnit={sensorUnit} airSensors={airSensors} units={units} />);
  if (finish.max_seconds) {
    const fails = finish.on_max === "fail";
    const words = `at most ${secondsText(finish.max_seconds)}${fails ? ", then fails" : ""}`;
    parts.push(
      <span className="sx-glyphs" role="img" aria-label={words} title={words}>
        <SignalGlyph shape="clock" />
        {secondsText(finish.max_seconds)}
        {fails ? <span className="sx-glyph-fail">✕</span> : null}
      </span>,
    );
  }
  if (parts.length === 0) return <>?</>;
  return (
    <span className="sx-glyph-line">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="sx-glyph-or">or</span>}
          {part}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * How a step ends, whichever comes first: its operations completing, the
 * maximum time (ending it or failing it), and conditions, each a line that
 * opens to its fields. Rows of a `SheetTable`, and the add menu after.
 */
export function EndpointFields({
  step,
  sensors,
  sensorUnit,
  airSensors,
  names,
  meters,
  vessels,
  onChange,
}: {
  step: Step;
  sensors: readonly NodeDocument[];
  sensorUnit: SensorUnit;
  /** Sensors that detect air in the line: what "a fluid runs out" reads. */
  airSensors: ReadonlySet<string>;
  names: Readonly<Record<string, string>>;
  /** What a volume can be counted through, first the likeliest: flow sensors, pumps, then parts liquid passes. */
  meters: readonly { value: string; label: string }[];
  /** The bags, reservoirs and chambers: what a step can run empty (D62). */
  vessels: readonly { value: string; label: string }[];
  onChange: (finish: Finish) => void;
}) {
  const finish = step.finish ?? {};
  const conditions = finish.conditions ?? [];
  const set = (change: Partial<Finish>) => onChange({ ...finish, ...change });
  const setCondition = (at: number, next: Condition) => set({ conditions: conditions.map((c, i) => (i === at ? next : c)) });
  const completing = completingOperations(step.actions ?? []).length > 0;
  const sensorOptions = sensors.map((node) => ({ value: node.local_id, label: node.label || node.local_id }));
  const bounded = Boolean(finish.max_seconds) || (Boolean(finish.complete) && completing);
  const units = useLiquidUnits();
  const [added, setAdded] = useState<number | null>(null);
  const air = [...airSensors][0];
  const adds: { value: EndpointKind; label: string }[] = [
    ...(sensors.length ? [{ value: "sensed" as const, label: "a sensor reads" }] : []),
    ...(air ? [{ value: "runs_out" as const, label: "a fluid runs out (air after liquid)" }] : []),
    ...(air ? [{ value: "arrives" as const, label: "a fluid arrives (liquid after air)" }] : []),
    ...(sensors.length ? [{ value: "pressure_threshold" as const, label: "a pressure" }] : []),
    { value: "volume_delivered", label: "a volume delivered" },
    ...(vessels.length ? [{ value: "vessel_empty" as const, label: "a vessel runs empty" }] : []),
    { value: "operator_wait", label: "the operator continues" },
  ];
  return (
    <>
      <SheetTable label="How it ends">
        <SheetRows>
          {(completing || finish.complete) && (
            <CheckRow label="Completion" checked={Boolean(finish.complete)} onChange={(complete) => set({ complete })} />
          )}
          <NumberField label="At most" unit="s" min={0} value={finish.max_seconds ?? null} onChange={(max) => set({ max_seconds: max || null })} />
          {finish.max_seconds ? (
            <Select
              label="Then"
              value={finish.on_max ?? "continue"}
              options={[
                { value: "continue", label: "end the step" },
                { value: "fail", label: "fail the step" },
              ]}
              onChange={(on_max) => set({ on_max })}
            />
          ) : null}
        </SheetRows>
        {conditions.map((condition, at) => (
          <FoldRow
            key={at}
            label={`Condition ${at + 1}`}
            summary={<ConditionGlyphs condition={condition} names={names} sensorUnit={sensorUnit} airSensors={airSensors} units={units} />}
            defaultOpen={added === at}
            onRemove={() => set({ conditions: conditions.filter((_, i) => i !== at) })}
          >
            {condition.kind === "sensed" && (
              <>
                <Select label="Sensor" value={condition.sensor} options={sensorOptions} onChange={(sensor) => setCondition(at, { ...condition, sensor })} />
                <Select
                  label="Measure"
                  value={condition.measure ?? "reading"}
                  options={MEASURES}
                  onChange={(measure) => setCondition(at, { ...condition, measure: measure === "reading" ? undefined : measure })}
                />
                <Select label="Is" value={condition.comparison} options={COMPARISONS} onChange={(comparison) => setCondition(at, { ...condition, comparison })} />
                <NumberField
                  label="Value"
                  unit={sensorUnit(condition.sensor).label.startsWith("(") ? undefined : sensorUnit(condition.sensor).label}
                  factor={sensorUnit(condition.sensor).factor}
                  value={condition.value}
                  onChange={(value) => value !== null && setCondition(at, { ...condition, value })}
                />
              </>
            )}
            {condition.kind === "pressure_threshold" && (
              <>
                <Select label="At" value={condition.at} options={sensorOptions} onChange={(where) => setCondition(at, { ...condition, at: where })} />
                <Select label="Is" value={condition.comparison} options={COMPARISONS} onChange={(comparison) => setCondition(at, { ...condition, comparison })} />
                <NumberField label="Gauge" unit="kPa" factor={1000} value={condition.pressure} onChange={(pressure) => pressure !== null && setCondition(at, { ...condition, pressure })} />
              </>
            )}
            {(condition.kind === "sensed" || condition.kind === "pressure_threshold") && (
              <>
                <Select
                  label="Trigger"
                  value={condition.edge ? "edge" : "level"}
                  options={[
                    { value: "level", label: "level" },
                    { value: "edge", label: condition.comparison === "ge" || condition.comparison === "gt" ? "rising edge" : "falling edge" },
                  ]}
                  onChange={(trigger) => setCondition(at, { ...condition, edge: trigger === "edge" || undefined })}
                />
                <NumberField label="Then hold" unit="s" placeholder="—" min={0} value={condition.hold ?? null} onChange={(hold) => setCondition(at, { ...condition, hold: hold || null })} />
                <NumberField label="Ignore under" unit="s" placeholder="—" min={0} value={condition.ignore ?? null} onChange={(ignore) => setCondition(at, { ...condition, ignore: ignore || null })} />
              </>
            )}
            {condition.kind === "volume_delivered" && (
              <>
                <Select
                  label="Through"
                  value={condition.through}
                  options={condition.through && meters.some((meter) => meter.value === condition.through) ? meters : [{ value: condition.through, label: condition.through || "choose…" }, ...meters]}
                  onChange={(through) => through && setCondition(at, { ...condition, through })}
                />
                <VolumeField label="Volume" value={condition.volume} onChange={(volume) => volume && setCondition(at, { ...condition, volume })} />
              </>
            )}
            {condition.kind === "vessel_empty" && (
              <Select label="Vessel" value={condition.vessel} options={vessels} onChange={(vessel) => setCondition(at, { ...condition, vessel })} />
            )}
            {condition.kind === "operator_wait" && (
              <TextField label="Instruction" area value={condition.instruction} onChange={(instruction) => setCondition(at, { ...condition, instruction: instruction || "Continue" })} />
            )}
          </FoldRow>
        ))}
        {!bounded && (
          <SheetRows>
            <FieldRow label="" span>
              <span className="sx-problem" role="alert">
                Needs a maximum time, or an operation that completes (R7.4).
              </span>
            </FieldRow>
          </SheetRows>
        )}
      </SheetTable>
      <AddSelect
        label="Endpoint"
        options={adds}
        onAdd={(kind) => {
          const sensor = kind === "runs_out" || kind === "arrives" ? air! : (sensors[0]?.local_id ?? "");
          setAdded(conditions.length);
          set({ conditions: [...conditions, freshCondition(kind as EndpointKind, sensor, units, meters[0]?.value, vessels[0]?.value)] });
        }}
      />
    </>
  );
}
